import React, { useEffect, useState, useRef } from "react";
import { Card, Form } from "react-bootstrap";
import Accordion from "react-bootstrap/Accordion";
import { DE } from "../../../Languages/DE";
import { EN } from "../../../Languages/EN";
import LanguageContext from "../../../helper/LanguageContext";
import { KJUUP_COLORS } from "../../../helper/constants";
import {
  BRANCH_CATEGORY_I18N,
  BRANCH_ROLE_I18N,
  COMPANY_BRANCH_CATEGORY_I18N,
  COMPANY_BRANCH_I18N,
  localizeBranchLabel,
  normalizeBranchLang,
} from "../../../Languages/vars/branchI18n";
import "./CompanyRoles.css";

/** Localized display name for a branch/role item (falls back to DE/EN). */
const getItemLabel = (item, lang) => {
  if (!item) return "";
  const key = item.name_en;
  if (
    key &&
    (BRANCH_ROLE_I18N[key] ||
      COMPANY_BRANCH_I18N[key] ||
      BRANCH_CATEGORY_I18N[key] ||
      COMPANY_BRANCH_CATEGORY_I18N[key])
  ) {
    return localizeBranchLabel(key, lang);
  }
  return lang === "de" ? (item.name_de || item.name_en || "") : (item.name_en || item.name_de || "");
};

function CompanyRoles(props) {
  const { isGerm, strings, language } = React.useContext(LanguageContext);
  const lang = normalizeBranchLang(language || (isGerm ? "de" : "en"));
  const isKjuup = props.isKjuup;
  const isSearchMode = props.searchMode; // New prop to enable search-only mode
  const containerClasses = [
    "company_section",
    "company_list",
    "setting_profile",
    "register_role_option",
    props.containerClassName,
  ]
    .filter(Boolean)
    .join(" ");
  const [display, setDisplay] = useState(null);
  const [search, setSearch] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [flatRoles, setFlatRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const inputRef = useRef(null);
  
  const handleClass = (index) => {
    console.log(display);
    if (display === index) {
      setDisplay(null);
    } else {
      setDisplay(index);
    }
  };
  const [selectRoles, setSelectRoles] = useState([]);
  const initialRolesRef = useRef(null);
  const isInitialMount = useRef(true);
  
  // Flatten all roles for search mode
  useEffect(() => {
    if (props.position) {
      const allRoles = [];
      props.position.forEach(category => {
        category.branches.forEach(branch => {
          allRoles.push({
            ...branch,
            category_de: category.name_de,
            category_en: category.name_en,
            label: getItemLabel(branch, lang),
            categoryLabel: getItemLabel(category, lang),
          });
        });
      });
      // Sort alphabetically in the active UI language
      allRoles.sort((a, b) =>
        (a.label || "").localeCompare(b.label || "", lang)
      );
      setFlatRoles(allRoles);
    }
  }, [props.position, lang]);
  
  useEffect(() => {
    if (props.selectedRoles) {
      const roles = typeof props.selectedRoles === 'string'
        ? props.selectedRoles.split(',').map(r => r.trim()).filter(Boolean)
        : props.selectedRoles;
      setSelectRoles(roles);
      // Only capture initial roles on first mount
      if (isInitialMount.current) {
        initialRolesRef.current = [...roles];
        isInitialMount.current = false;
      }
    } else {
      setSelectRoles([]);
      // Only capture initial roles on first mount
      if (isInitialMount.current) {
        initialRolesRef.current = [];
        isInitialMount.current = false;
      }
    }
  }, [props.selectedRoles]);

  const addRoles = async (value, roleData) => {
    let roles = [...selectRoles];
    // Use English name for consistency (that's what's stored in the database)
    const roleName = roleData?.name_en || value;
    const exist = roles.includes(roleName);
    let updatedRoles;
    if (!exist) {
      roles.push(roleName);
      updatedRoles = roles;
      setSelectRoles(roles);
      if (roles.length > 0) {
        props.setRoles && props.setRoles(true);
      }
    } else {
      updatedRoles = roles.filter((role) => role !== roleName);
      setSelectRoles(updatedRoles);
      if (updatedRoles.length < 1) {
        props.setRoles && props.setRoles(false);
      }
    }
    // Update parent immediately
    if (props.onRolesChange) {
      props.onRolesChange(updatedRoles);
    }
    
    // In search mode, also call selectPosition immediately for each selection
    if (isSearchMode) {
      props.selectPosition(updatedRoles);
    }
  };

  const removeRole = (roleName) => {
    const updatedRoles = selectRoles.filter(role => role !== roleName);
    setSelectRoles(updatedRoles);
    if (updatedRoles.length < 1) {
      props.setRoles && props.setRoles(false);
    }
    if (props.onRolesChange) {
      props.onRolesChange(updatedRoles);
    }
    if (isSearchMode) {
      props.selectPosition(updatedRoles);
    }
  };

  const [positions, setPositions] = useState(props.position);
  useEffect(() => {
    if (props.position) {
      const temp = props.position.map((item) => ({
        ...item,
        branches: [...(item.branches || [])].sort((a, b) =>
          getItemLabel(a, lang).localeCompare(getItemLabel(b, lang), lang)
        ),
      }));
      setPositions(temp);
    }
  }, [props.position, lang]);
  const allPositions = props.position;
  const sendRoles = () => {
    props.selectPosition(selectRoles);
    props.hideRoles();
  };

  // Search for roles in search mode (flat list)
  const searchRoleFlat = (text) => {
    setSearch(text);
    if (text.trim() !== "") {
      const q = text.toLowerCase();
      const filtered = flatRoles.filter((role) => {
        const localized = role.label || getItemLabel(role, lang);
        return (
          localized.toLowerCase().includes(q) ||
          role.name_en?.toLowerCase().includes(q) ||
          role.name_de?.toLowerCase().includes(q)
        );
      });
      setFilteredRoles(filtered);
      setShowResults(true);
    } else {
      setFilteredRoles([]);
      setShowResults(false);
    }
  };

  const searchRole = async (text) => {
    setSearch(text);
    console.log(typeof text);

    if (text !== "") {
      let filterPositions = allPositions;
      const q = text.toLowerCase();
      let positionIndex = await positions.findIndex(
        (item) => getItemLabel(item, lang)?.toLowerCase() == q
      );

      if (positionIndex !== -1) {
        console.log(positionIndex);
        filterPositions = [allPositions[positionIndex]];
        console.log(filterPositions);
        setDisplay(null);
      } else {
        filterPositions = await allPositions.map((item) => {
          console.log(positionIndex);
          let filterRoles = item.branches.filter((i) => {
            const localized = getItemLabel(i, lang);
            return (
              localized?.toLowerCase().includes(q) ||
              i.name_en?.toLowerCase().includes(q) ||
              i.name_de?.toLowerCase().includes(q)
            );
          });
          return { ...item, branches: filterRoles };
        });
        filterPositions = filterPositions.filter(
          (position) => position.branches.length > 0
        );
        if (filterPositions.length > 0) {
          setDisplay(filterPositions[0].id);
          console.log(filterPositions[0].id);
        }
      }
      setPositions(filterPositions);
    } else {
      setDisplay(null);
      setPositions(allPositions);
    }
  };

  // Get display name for a role (for showing selected tags)
  const getRoleDisplayName = (roleName) => {
    const role = flatRoles.find(r => r.name_en === roleName);
    if (role) {
      return role.label || getItemLabel(role, lang);
    }
    return localizeBranchLabel(roleName, lang) || roleName;
  };

  // Render search mode UI
  if (isSearchMode) {
    return (
      <div className="role-search-container">
        {/* Selected roles as tags */}
        {selectRoles.length > 0 && (
          <div className="role-tags-container">
            {selectRoles.map((role, index) => (
              <span key={index} className={isKjuup ? "role-tag role-tag-internal" : "role-tag"}>
                {getRoleDisplayName(role)}
                <button
                  type="button"
                  className="role-tag-remove"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    removeRole(role);
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        
        {/* Search input */}
        <div className="role-search-input-wrapper">
          <Form.Control
            ref={inputRef}
            autoComplete="off"
            value={search}
            type="text"
            placeholder={strings.role_in_the_building_industry}
            onChange={(e) => {
              e.preventDefault();
              searchRoleFlat(e.target.value);
            }}
            onFocus={() => {
              if (search.trim() !== "") {
                setShowResults(true);
              }
            }}
            className="role-search-input"
          />
        </div>
        
        {/* Search results dropdown */}
        {showResults && filteredRoles.length > 0 && (
          <div className="role-search-results">
            {filteredRoles.map((role, index) => {
              const isSelected = selectRoles.includes(role.name_en);
              return (
                <div
                  key={index}
                  className={`role-search-item ${isSelected ? 'role-search-item-selected' : ''} ${isKjuup ? 'role-search-item-internal' : ''}`}
                  onClick={() => {
                    addRoles(role.name_en, role);
                    setSearch("");
                    setShowResults(false);
                  }}
                >
                  <span className="role-search-item-name">
                    {role.label || getItemLabel(role, lang)}
                  </span>
                  <span className="role-search-item-category">
                    {role.categoryLabel ||
                      localizeBranchLabel(role.category_en, lang) ||
                      (lang === "de" ? role.category_de : role.category_en)}
                  </span>
                  {isSelected && (
                    <span className={isKjuup ? "role-search-item-check-internal" : "role-search-item-check"}>✓</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {showResults && search.trim() !== "" && filteredRoles.length === 0 && (
          <div className="role-search-results">
            <div className="role-search-no-results">
              {strings.no_results_found || (lang === "de" ? "Keine Ergebnisse gefunden" : "No results found")}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Original accordion-based UI
  return (
    <React.Fragment>
      <div
        className={containerClasses}
      >
        <div className="company_header">
          <div className="heading_icon"></div>
        </div>
        <div className="search company-roles__search">
          <Form.Group controlId="exampleForm.ControlInput1">
            <Form.Control
              autocomplete="off"
              value={search}
              type="search"
              placeholder={strings.search}
              onChange={(e) => {
                e.preventDefault();
                searchRole(e.target.value);
              }}
            />
          </Form.Group>
          {/* <img
            src={process.env.PUBLIC_URL + "/assets/img/search.svg"}
            alt="Generic placeholder"
          /> */}
        </div>
        <div className="setting_accrd">
          <Accordion className="max-h-position">
            {positions &&
              positions.map((position, index) => (
                <Card key={index}>
                  <Accordion.Toggle
                    key={index}
                    as={Card.Header}
                    variant="link"
                    eventKey={position.id}
                    onClick={(e) => handleClass(position.id)}
                  >
                    {getItemLabel(position, lang)}
                    {display === position.id && (
                      <img 
                        src={process.env.PUBLIC_URL + (isKjuup ? "/assets/img/arrow-down-modern-green.svg" : "/assets/img/arrow-down-modern-orange.svg")} 
                        alt="Toggle" 
                        className="arrow-icon acr_up"
                      />
                    )}
                    {display !== position.id && (
                      <img 
                        src={process.env.PUBLIC_URL + (isKjuup ? "/assets/img/arrow-right-modern-green.svg" : "/assets/img/arrow-right-modern-orange.svg")} 
                        alt="Toggle" 
                        className="arrow-icon acr_down"
                      />
                    )}
                  </Accordion.Toggle>
                  <Accordion.Collapse
                    className={display === position.id ? "show" : ""}
                    eventKey={position.id}
                  >
                    <Card.Body>
                      <div className="role-options">
                        <div className="role-main">
                          {position.branches.map((roles, key) => (
                            <label key={key} className={isKjuup ? 'role-label-internal' : "role-label"}>
                              <i>{getItemLabel(roles, lang)}</i>
                              <input
                                value={roles.name_en}
                                checked={selectRoles.includes(roles.name_en)}
                                onChange={(e) => {
                                  addRoles(e.target.value, roles);
                                }}
                                type="checkbox"
                              />
                              <span className={isKjuup ? "checkmark-label-internal" : "checkmark-label"} />
                            </label>
                          ))}
                        </div>
                      </div>
                    </Card.Body>
                  </Accordion.Collapse>
                </Card>
              ))}
            {positions.length <= 0 && (
              <Card className="text-center">
                <p className="text-muted">No Match Record Found!</p>
              </Card>
            )}
          </Accordion>
        </div>
        <div className={isKjuup ? "bottom-button-internal" : "bottom-button"}>
          <a
            className="pointer"
            onClick={(e) => {
              e.preventDefault();
              // Reset to initial values captured when dialog opened
              const resetRoles = initialRolesRef.current ? [...initialRolesRef.current] : [];
              setSelectRoles(resetRoles);
              // Update parent with original values
              if (props.onRolesChange) {
                props.onRolesChange(resetRoles);
              }
              props.hideRoles();
            }}
          >
            {strings.cancel || strings.add_company_cancel || (lang === "de" ? DE.add_company_cancel : EN.cancel)}
          </a>
          <a className="pointer" onClick={sendRoles}>
            {strings.ok}
          </a>
        </div>
      </div>
    </React.Fragment>
  );
}

export default CompanyRoles;
